import { useState, useEffect, useRef, useCallback } from 'react';
import { apiClient, useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Image, Download, Trophy, TrendingDown, TrendingUp, CalendarDays, Users, RefreshCw, Share2, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Spinner } from './ChallengeShared';
import { shareFile } from './ShareButtons';

export function VisuelsTab() {
  const { user } = useAuth();
  const [podium, setPodium] = useState(null);
  const [config, setConfig] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState('');
  const [visualType, setVisualType] = useState('podium');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const canvasRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [podRes, cfgRes, partRes] = await Promise.all([
        apiClient.get('/challenge/podium'),
        apiClient.get('/challenge/config'),
        apiClient.get('/challenge/dashboard'),
      ]);
      setPodium(podRes.data);
      setConfig(cfgRes.data);
      const actifs = (partRes.data.participants || []).filter(p => p.actif || p.caution_p1_ok);
      setParticipants(actifs);
      if (actifs.length > 0) setSelectedParticipant(actifs[0].id);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportImage = async () => {
    if (!canvasRef.current) return;
    setExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(canvasRef.current, {
        backgroundColor: '#0F0F0F',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const link = document.createElement('a');
      link.download = `lameute_${visualType}_${Date.now()}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Image telechargee !');
    } catch { toast.error('Erreur export'); }
    finally { setExporting(false); }
  };

  const shareVisual = async () => {
    if (!canvasRef.current) return;
    setExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(canvasRef.current, {
        backgroundColor: '#0F0F0F',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      const shared = await shareFile({
        blob,
        fileName: `lameute_${visualType}_${Date.now()}.png`,
        title: 'La Meute - Challenge Transformation',
        text: 'Challenge Transformation La Meute Polynesie',
      });
      if (!shared) {
        // Fallback: download + open WhatsApp with text
        const link = document.createElement('a');
        link.download = `lameute_${visualType}_${Date.now()}.png`;
        link.href = canvas.toDataURL('image/png');
        link.click();
        window.open(`https://wa.me/?text=${encodeURIComponent('Challenge Transformation La Meute Polynesie - Regarde le classement !')}`, '_blank');
        toast.info('Image telechargee — partage-la dans WhatsApp');
      }
    } catch { toast.error('Erreur partage'); }
    finally { setExporting(false); }
  };

  if (loading) return <Spinner />;

  const types = [
    { id: 'podium', label: 'Podium', icon: Trophy },
    { id: 'participant', label: 'Fiche Challenger', icon: Users },
    { id: 'pesee', label: 'Annonce Pesee', icon: CalendarDays },
  ];

  const selectedP = participants.find(p => p.id === selectedParticipant);

  return (
    <div className="p-4 pb-24 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-white flex items-center gap-2" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
          <Image className="w-5 h-5 text-[#FF6B35]" /> Visuels Facebook
        </h2>
        <Button size="sm" onClick={fetchData} variant="outline" className="border-zinc-700 text-zinc-400">
          <RefreshCw className="w-3 h-3 mr-1" /> Rafraichir
        </Button>
      </div>

      {/* Type selector */}
      <div className="flex gap-2" data-testid="visual-type-selector">
        {types.map(t => (
          <button key={t.id} onClick={() => setVisualType(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
              visualType === t.id ? 'bg-[#FF6B35] text-white' : 'bg-[#1B1B1B] text-zinc-400 border border-[#2A2A2E]'
            }`} data-testid={`visual-type-${t.id}`}>
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* Participant selector for "participant" type */}
      {visualType === 'participant' && (
        <select value={selectedParticipant} onChange={e => setSelectedParticipant(e.target.value)}
          className="w-full h-10 bg-[#1B1B1B] border border-[#2A2A2E] text-white rounded-lg px-3 text-sm"
          data-testid="visual-participant-select">
          {participants.map(p => (
            <option key={p.id} value={p.id}>{p.nom}</option>
          ))}
        </select>
      )}

      {/* Preview — scaled to fit on mobile, scrollable as fallback */}
      <div className="border border-[#2A2A2E] rounded-lg overflow-hidden" data-testid="visual-preview">
        <div className="overflow-x-auto">
          <div ref={canvasRef} className="visual-export-container" style={{ transformOrigin: 'top left' }}>
            {visualType === 'podium' && <PodiumVisual podium={podium} config={config} />}
            {visualType === 'participant' && selectedP && <ParticipantVisual participant={selectedP} config={config} />}
            {visualType === 'pesee' && <PeseeVisual config={config} />}
          </div>
        </div>
      </div>
      <p className="text-[9px] text-zinc-600 text-center">Glisse horizontalement pour voir le visuel complet</p>

      {/* Export + Share */}
      <div className="flex gap-2">
        <Button onClick={exportImage} disabled={exporting} className="flex-1 bg-[#FF6B35] text-white hover:bg-[#FF8C55]" data-testid="export-visual-btn">
          <Download className="w-4 h-4 mr-2" /> {exporting ? 'Export...' : 'PNG'}
        </Button>
        <Button onClick={shareVisual} disabled={exporting} className="flex-1 bg-[#25D366] text-white hover:bg-[#25D366]/80" data-testid="share-visual-btn">
          <Share2 className="w-4 h-4 mr-2" /> Partager
        </Button>
      </div>
    </div>
  );
}

/* ═══ PODIUM VISUAL ═══ */
function PodiumVisual({ podium, config }) {
  if (!podium) return <div className="p-8 text-center text-zinc-500">Aucune donnee</div>;
  const medals = ['#FFD700', '#C0C0C0', '#CD7F32'];
  const sections = [
    { key: 'recomposition', title: 'RECOMPOSITION', icon: '🏆', scoreKey: 'score_prix1' },
    { key: 'perte_gras', title: 'PERTE DE GRAS', icon: '🔥', scoreKey: 'score_mg' },
    { key: 'prise_muscle', title: 'PRISE DE MUSCLE', icon: '💪', scoreKey: 'score_mm' },
  ];

  return (
    <div style={{ background: '#0F0F0F', padding: '24px 16px', fontFamily: "'Manrope', sans-serif", minWidth: 340 }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 11, letterSpacing: 4, color: '#FF6B35', fontWeight: 700, textTransform: 'uppercase' }}>La Meute Polynesie</div>
        <div style={{ fontSize: 28, fontWeight: 900, color: '#FFFFFF', fontFamily: "'Bebas Neue', sans-serif", marginTop: 4 }}>CHALLENGE TRANSFORMATION</div>
        <div style={{ fontSize: 11, color: '#A1A1AA', marginTop: 4 }}>
          {config?.date_j0} → {config?.date_s12} · CLASSEMENT EN DIRECT
        </div>
      </div>

      {/* Sections stacked vertically */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {sections.map(s => (
          <div key={s.key} style={{ background: '#1B1B1B', borderRadius: 8, padding: 12, border: '1px solid #2A2A2E' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 18 }}>{s.icon}</span>
              <div style={{ fontSize: 10, fontWeight: 800, color: '#FF6B35', letterSpacing: 2 }}>{s.title}</div>
            </div>
            {(podium[s.key] || []).slice(0, 3).map((e, i) => (
              <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: i > 0 ? '1px solid #2A2A2E' : 'none' }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: medals[i] || '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#000', flexShrink: 0 }}>
                  {i + 1}
                </div>
                <span style={{ fontSize: 12, color: '#FFFFFF', fontWeight: 600, flex: 1 }}>{e.nom}</span>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#FF6B35', fontFamily: "'DM Mono', monospace" }}>{e[s.scoreKey]?.toFixed(1)}</span>
              </div>
            ))}
            {(podium[s.key] || []).length === 0 && (
              <div style={{ textAlign: 'center', color: '#555', fontSize: 11, padding: 8 }}>En attente</div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ textAlign: 'center', marginTop: 16, fontSize: 9, color: '#555' }}>
        lameute-aracom.fr · Scores mis a jour automatiquement
      </div>
    </div>
  );
}

/* ═══ PARTICIPANT VISUAL ═══ */
function ParticipantVisual({ participant: p, config }) {
  const last = p.derniere_mesure;
  const diffPoids = last ? (last.poids - (p.poids_j0 || 0)).toFixed(1) : '—';
  const diffMG = last ? (last.mg - (p.mg_j0 || 0)).toFixed(1) : '—';
  const diffMM = last ? (last.mm - (p.mm_j0 || 0)).toFixed(1) : '—';

  return (
    <div style={{ background: '#0F0F0F', padding: '24px 16px', fontFamily: "'Manrope', sans-serif", minWidth: 320 }}>
      <div style={{ textAlign: 'center', marginBottom: 6 }}>
        <div style={{ fontSize: 10, letterSpacing: 4, color: '#FF6B35', fontWeight: 700 }}>LA MEUTE POLYNESIE</div>
      </div>

      {/* Name */}
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'linear-gradient(135deg, #FF00B8, #FF6B35, #FFB800)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px', fontSize: 24, fontWeight: 900, color: '#FFF' }}>
          {p.nom?.charAt(0)}
        </div>
        <div style={{ fontSize: 24, fontWeight: 900, color: '#FFFFFF', fontFamily: "'Bebas Neue', sans-serif" }}>{p.nom}</div>
        <div style={{ fontSize: 10, color: '#A1A1AA' }}>
          Objectif: {p.objectif === 'gras' ? 'Perte de gras' : 'Prise de muscle'} ({p.pct_objectif}%)
        </div>
      </div>

      {/* Stats grid */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'POIDS', j0: p.poids_j0, now: last?.poids, diff: diffPoids, unit: 'kg', color: '#FF6B35' },
          { label: 'GRAS', j0: p.mg_j0, now: last?.mg, diff: diffMG, unit: '%', color: '#FF00B8' },
          { label: 'MUSCLE', j0: p.mm_j0, now: last?.mm, diff: diffMM, unit: 'kg', color: '#3b82f6' },
        ].map(s => (
          <div key={s.label} style={{ flex: 1, background: '#1B1B1B', borderRadius: 8, padding: 10, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: 9, color: '#A1A1AA', letterSpacing: 2, fontWeight: 700, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 10, color: '#888' }}>J0: {s.j0 ?? '—'} {s.unit}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#FFF', fontFamily: "'DM Mono', monospace", margin: '2px 0' }}>{s.now ?? '—'}</div>
            <div style={{ fontSize: 11, color: parseFloat(s.diff) <= 0 && s.label !== 'MUSCLE' ? '#2e7d32' : parseFloat(s.diff) > 0 && s.label === 'MUSCLE' ? '#2e7d32' : '#ef4444', fontWeight: 700 }}>
              {s.diff !== '—' ? `${parseFloat(s.diff) > 0 ? '+' : ''}${s.diff} ${s.unit}` : '—'}
            </div>
          </div>
        ))}
      </div>

      {/* Score */}
      {p.score_prix1 !== undefined && (
        <div style={{ textAlign: 'center', background: '#1B1B1B', borderRadius: 8, padding: 12, border: '1px solid #2A2A2E' }}>
          <div style={{ fontSize: 9, color: '#A1A1AA', letterSpacing: 2, fontWeight: 700 }}>SCORE RECOMPOSITION</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: '#FF6B35', fontFamily: "'DM Mono', monospace" }}>{p.score_prix1?.toFixed(1)}</div>
          <div style={{ fontSize: 10, color: '#888' }}>MG: {p.score_mg?.toFixed(1)} · MM: {p.score_mm?.toFixed(1)}</div>
        </div>
      )}

      <div style={{ textAlign: 'center', marginTop: 14, fontSize: 9, color: '#555' }}>
        Challenge Transformation · {config?.date_j0} → {config?.date_s12}
      </div>
    </div>
  );
}

/* ═══ PESEE ANNOUNCEMENT VISUAL ═══ */
function PeseeVisual({ config }) {
  if (!config) return <div className="p-8 text-center text-zinc-500">Aucune config</div>;

  const dates = [
    { label: 'J0', date: config.date_j0 },
    { label: 'S2', date: config.date_s2 },
    { label: 'S4', date: config.date_s4 },
    { label: 'S6', date: config.date_s6 },
    { label: 'S8', date: config.date_s8 },
    { label: 'S10', date: config.date_s10 },
    { label: 'S12', date: config.date_s12 },
  ];

  // Find next pesee
  const today = new Date(Date.now() - 10 * 3600000).toISOString().slice(0, 10);
  const nextPesee = dates.find(d => d.date >= today) || dates[dates.length - 1];
  const daysUntil = nextPesee ? Math.ceil((new Date(nextPesee.date) - new Date()) / 86400000) : 0;

  return (
    <div style={{ background: '#0F0F0F', padding: '32px 16px', fontFamily: "'Manrope', sans-serif", minWidth: 320, textAlign: 'center' }}>
      <div style={{ fontSize: 11, letterSpacing: 4, color: '#FF6B35', fontWeight: 700 }}>LA MEUTE POLYNESIE</div>
      <div style={{ fontSize: 32, fontWeight: 900, color: '#FFFFFF', fontFamily: "'Bebas Neue', sans-serif", margin: '6px 0' }}>
        PESEE OFFICIELLE
      </div>

      {/* Big countdown */}
      <div style={{ margin: '16px auto', width: 100, height: 100, borderRadius: '50%', background: 'linear-gradient(135deg, #FF00B8, #FF6B35)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 40, fontWeight: 900, color: '#FFF', fontFamily: "'DM Mono', monospace", lineHeight: 1 }}>
          {daysUntil <= 0 ? 'J' : `J-${daysUntil}`}
        </div>
        {daysUntil > 0 && <div style={{ fontSize: 9, color: '#FFF', opacity: 0.8 }}>jours</div>}
      </div>

      <div style={{ fontSize: 18, fontWeight: 800, color: '#FFFFFF', marginBottom: 4 }}>
        Palier {nextPesee?.label}
      </div>
      <div style={{ fontSize: 14, color: '#FF6B35', fontWeight: 600, marginBottom: 16 }}>
        {nextPesee?.date ? new Date(nextPesee.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''}
      </div>

      {/* Rules reminder */}
      <div style={{ background: '#1B1B1B', borderRadius: 8, padding: 12, textAlign: 'left', border: '1px solid #2A2A2E', maxWidth: 320, margin: '0 auto' }}>
        <div style={{ fontSize: 9, color: '#FF6B35', fontWeight: 700, letterSpacing: 2, marginBottom: 6 }}>RAPPEL</div>
        {['A jeun (pas de petit-dej)', 'Meme balance Tanita', 'Meme heure', 'Photo de la balance obligatoire'].map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 11, color: '#FFFFFF' }}>
            <span style={{ color: '#2e7d32', fontSize: 13 }}>✓</span> {r}
          </div>
        ))}
      </div>

      {/* Timeline mini */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
        {dates.map(d => {
          const isPast = d.date < today;
          const isCurrent = d.label === nextPesee?.label;
          return (
            <div key={d.label} style={{
              padding: '3px 8px', borderRadius: 4, fontSize: 9, fontWeight: 700,
              background: isCurrent ? '#FF6B35' : isPast ? '#2A2A2E' : '#1B1B1B',
              color: isCurrent ? '#FFF' : isPast ? '#555' : '#A1A1AA',
              border: `1px solid ${isCurrent ? '#FF6B35' : '#2A2A2E'}`,
            }}>
              {d.label}
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 16, fontSize: 9, color: '#555' }}>
        Challenge Transformation · {config?.date_j0} → {config?.date_s12}
      </div>
    </div>
  );
}
